import type { TicketActorType, TicketReply } from "../../models/Ticket";

export const TICKET_TYPE_LABELS: Record<string, string> = {
  "0": "客服",
  "1": "货源",
  "2": "订单",
  customer: "客服",
  cargo: "货源",
  order: "订单",
};

export const TICKET_TYPE_COLORS: Record<string, "default" | "primary" | "success" | "warning" | "info"> = {
  "0": "default",
  "1": "primary",
  "2": "success",
  customer: "default",
  cargo: "primary",
  order: "success",
};

export const getTicketTypeLabel = (rawType?: string | number | null): string => {
  if (rawType === undefined || rawType === null || rawType === "") {
    return "客服";
  }
  const key = String(rawType);
  return TICKET_TYPE_LABELS[key] ?? rawType;
};

export const getTicketTypeColor = (rawType?: string | number | null): "default" | "primary" | "success" | "warning" | "info" => {
  if (rawType === undefined || rawType === null || rawType === "") {
    return "default";
  }
  const key = String(rawType);
  return TICKET_TYPE_COLORS[key] ?? "default";
};

/**
 * 判断工单是否为货源类型。
 * @param rawType 工单类型
 * @returns 是否为货源工单
 */
export const isCargoTicketType = (rawType?: string | number | null): boolean => {
  const key = String(rawType ?? "");
  return key === "1" || key === "cargo";
};

/**
 * 判断工单是否为订单类型。
 * @param rawType 工单类型
 * @returns 是否为订单工单
 */
export const isOrderTicketType = (rawType?: string | number | null): boolean => {
  const key = String(rawType ?? "");
  return key === "2" || key === "order";
};

export interface TicketRelatedDetailTarget {
  /** 后台详情页路径 */
  path: string;
  /** 按钮文案 */
  label: string;
}

/**
 * 解析货源 / 订单工单可跳转的详情目标。
 * @param type 工单类型
 * @param subject 关联对象 ID（货源 ID 或订单 ID）
 * @returns 可跳转目标；无法跳转时返回 null
 */
export const getTicketRelatedDetailTarget = (
  type?: string | number | null,
  subject?: string | null
): TicketRelatedDetailTarget | null => {
  const relatedId = (subject ?? "").trim();
  if (!relatedId) {
    return null;
  }
  if (isCargoTicketType(type)) {
    return {
      path: `/validCargos/${relatedId}/show`,
      label: "货源详情",
    };
  }
  if (isOrderTicketType(type)) {
    return {
      path: `/waybills/${relatedId}/show`,
      label: "订单详情",
    };
  }
  return null;
};

export const isReplyFromCurrentUser = (
  reply: TicketReply,
  currentUserId?: string | null
): boolean => {
  if (reply.isSelf) {
    return true;
  }
  if (reply.senderId === "self") {
    return true;
  }
  if (currentUserId && reply.senderId && reply.senderId === currentUserId) {
    return true;
  }
  return false;
};

export const isAiReply = (reply: TicketReply): boolean => {
  if (reply.isAi) {
    return true;
  }
  if (reply.senderType === "ai") {
    return true;
  }
  return false;
};

/**
 * 根据用户 role 解析业务身份文案（与用户列表一致：1 货主，其他司机）。
 * @param role 用户角色
 * @returns 身份文案，无法识别时返回空串
 */
export const getUserIdentityLabel = (role?: number | null): string => {
  if (role === undefined || role === null || Number.isNaN(role)) {
    return "";
  }
  return role === 1 ? "货主" : "司机";
};

/**
 * 解析回复发送人的身份展示文案（AI / 货主 / 司机 / 客服等）。
 * @param reply 回复
 * @param roleOverride 从用户资料补全的 role（优先于 reply.senderRole）
 * @returns 身份文案
 */
export const getReplySenderIdentityLabel = (
  reply: TicketReply,
  roleOverride?: number | null
): string => {
  if (isAiReply(reply)) {
    return "AI";
  }
  if (reply.senderType === "staff" || reply.senderType === "system") {
    return reply.senderType === "staff" ? "客服" : "系统";
  }
  const role = roleOverride ?? reply.senderRole;
  return getUserIdentityLabel(role);
};

export const getSenderDisplayName = (
  reply: TicketReply,
  currentUserId?: string | null
): string => {
  if (isAiReply(reply)) {
    return reply.senderName || "AI 助手";
  }
  if (isReplyFromCurrentUser(reply, currentUserId)) {
    return reply.senderName || "我";
  }
  return reply.senderName || reply.senderId || "未知发送人";
};

export const getActorType = (
  reply: TicketReply,
  currentUserId?: string | null
): TicketActorType => {
  if (isAiReply(reply)) {
    return "ai";
  }
  if (isReplyFromCurrentUser(reply, currentUserId)) {
    return "user";
  }
  if (reply.senderType) {
    return reply.senderType;
  }
  return "staff";
};

export const REPORT_STATUS_LABELS: Record<string, string> = {
  pending: "待处理",
  waiting: "待处理",
  "0": "待处理",
  processed: "已处理",
  handled: "已处理",
  done: "已处理",
  "1": "已处理",
  restored: "已恢复",
  "2": "已恢复",
  dismissed: "已驳回",
  rejected: "已驳回",
  "-1": "已驳回",
};

export const REPORT_REASON_LABELS: Record<string, string> = {
  spam: "垃圾信息",
  ads: "广告",
  abuse: "辱骂",
  insult: "辱骂",
  porn: "色情",
  fraud: "欺诈",
  other: "其他",
};

/**
 * 将举报状态转换为中文文案。
 * @param status 原始状态
 * @returns 展示文案
 */
export const getReportStatusLabel = (status?: string | number | null): string => {
  if (status === undefined || status === null || status === "") {
    return "待处理";
  }
  const key = String(status);
  return REPORT_STATUS_LABELS[key] ?? key;
};

/**
 * 将举报状态映射为 Chip 颜色。
 * @param status 原始状态
 * @returns MUI Chip 颜色
 */
export const getReportStatusColor = (
  status?: string | number | null
): "default" | "warning" | "success" | "info" | "error" => {
  const key = String(status ?? "").toLowerCase();
  if (key === "restored" || key === "2") {
    return "success";
  }
  if (key === "processed" || key === "handled" || key === "done" || key === "1") {
    return "info";
  }
  if (key === "dismissed" || key === "rejected" || key === "-1") {
    return "default";
  }
  return "warning";
};

/**
 * 将举报名因转换为中文文案。
 * @param reason 原始原因
 * @returns 展示文案
 */
export const getReportReasonLabel = (reason?: string | null): string => {
  const trimmed = (reason ?? "").trim();
  if (!trimmed) {
    return "未填写原因";
  }
  return REPORT_REASON_LABELS[trimmed] ?? REPORT_REASON_LABELS[trimmed.toLowerCase()] ?? trimmed;
};

/**
 * 判断举报记录对应的回复是否可恢复。
 * @param status 举报状态
 * @returns 是否展示恢复操作
 */
export const canRestoreReportedReply = (status?: string | number | null): boolean => {
  const key = String(status ?? "").toLowerCase();
  return key !== "restored" && key !== "2";
};

